import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Ably from 'ably';
import { ChatClient, type ChatMessageEvent, type Room, type Message as AblyMessage, Message, OrderBy } from '@ably/chat';
import { getSupabaseClient } from '../../lib/supabase';
import { seekerDataService } from '../../services/seekerDataService';

type TabKey = 'home' | 'profile';

const tabDefinitions: Array<{
  key: TabKey;
  label: string;
  icon: React.ReactNode;
  path: string;
}> = [
  {
    key: 'home',
    label: 'Home',
    path: '/home',
    icon: (
      <svg className="h-6 w-6" fill="currentColor" viewBox="0 0 20 20">
        <path d="M10.707 2.293a1 1 0 00-1.414 0l-7 7a1 1 0 001.414 1.414L4 10.414V17a1 1 0 001 1h2a1 1 0 001-1v-2a1 1 0 011-1h2a1 1 0 011 1v2a1 1 0 001 1h2a1 1 0 001-1v-6.586l.293.293a1 1 0 001.414-1.414l-7-7z" />
      </svg>
    )
  },
  {
    key: 'profile',
    label: 'Profile',
    path: '/home/profile',
    icon: (
      <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
      </svg>
    )
  }
];

type UiMessage = {
  id: string;
  text: string;
  time: string;
  timestamp: number;
  fromMe?: boolean;
  serial?: string;
  deleted?: boolean;
};

const DEFAULT_GUIDE_AVATAR = 'http://localhost:3845/assets/ab29c58d5d0211dcf49eb600497756c2bd84e2e2.svg';

type AvatarProps = {
  src?: string;
  alt: string;
  fallback?: string;
};

function Avatar({ src, alt, fallback }: AvatarProps) {
  return (
    <div className="flex h-8 w-8 shrink-0 items-center justify-center overflow-hidden rounded-full border border-[#2C2731]/30 bg-[#E8DFBD]">
      {src ? (
        <img src={src} alt={alt} className="h-full w-full object-cover" />
      ) : (
        <span className="text-sm font-medium text-[#2C2731]">{fallback}</span>
      )}
    </div>
  );
}

type GuideSummary = {
  id?: string; // Guide's auth user ID
  name?: string;
  image?: string | null;
  status?: string;
};

type ChatProps = {
  onBack?: () => void;
  guide?: GuideSummary | null;
};

type ConnectionState = 'connecting' | 'connected' | 'failed';

function getInitials(name: string) {
  const parts = name
    .trim()
    .split(/\s+/)
    .filter(Boolean);

  if (parts.length === 0) {
    return 'GC';
  }

  if (parts.length === 1) {
    return parts[0].slice(0, 2).toUpperCase();
  }

  return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
}

export default function Chat({ onBack, guide }: ChatProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const [messages, setMessages] = useState<UiMessage[]>([]);
  const [input, setInput] = useState('');
  const [connected, setConnected] = useState<ConnectionState>('connecting');
  const [occupancyCount, setOccupancyCount] = useState<number>(0);
  const [seekerProfile, setSeekerProfile] = useState<{ name?: string; imageUrl?: string } | null>(null);

  const activeTab: TabKey | null = null;

  const handleTabClick = (path: string) => {
    if (location.pathname === path) return;
    navigate(path);
  };

  const ablyRef = useRef<Ably.Realtime | null>(null);
  const chatClientRef = useRef<ChatClient | null>(null);
  const roomRef = useRef<Room | null>(null);

  const listRef = useRef<HTMLDivElement | null>(null);

  const supabase = useMemo(() => getSupabaseClient(), []);

  const guideImage = guide?.image ?? '';
  const hasGuideImage = guideImage.trim().length > 0;
  const guideAvatarSrc = hasGuideImage ? guideImage : DEFAULT_GUIDE_AVATAR;

  const rawGuideName = guide?.name?.trim() ?? '';
  const guideName =
    rawGuideName.length > 0 ? rawGuideName : guide ? 'Your Guide' : 'GuidesCollective';
  const guideInitials = useMemo(() => getInitials(guideName), [guideName]);
  const guideStatus = guide?.status ?? (guide ? 'Online now' : 'Awaiting match');

  // Load seeker profile and clear message notifications on mount
  useEffect(() => {
    (async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (user) {
          // Load seeker profile for avatar
          try {
            const { profile } = await seekerDataService.getCurrentUser();
            if (profile) {
              setSeekerProfile({ name: profile.name, imageUrl: profile.imageUrl });
            }
          } catch (err) {
            console.warn('Failed to load seeker profile', err);
          }

          // Clear message notifications from this guide
          if (guide?.id) {
            try {
              await supabase.functions.invoke('clear-message-notification', {
                body: { sender_id: guide.id }
              });
            } catch (err) {
              console.warn('Failed to clear message notifications', err);
            }
          }
        }
      } catch (err) {
        console.warn('Failed to fetch current user data', err);
      }
    })();
  }, [supabase, guide?.id]);

  useEffect(() => {
    if (!listRef.current) return;
    listRef.current.scrollTo({ top: listRef.current.scrollHeight, behavior: 'smooth' });
  }, [messages.length]);

  useEffect(() => {
    let cancelled = false;
    let unsubscribeConnection: (() => void) | undefined;
    let unsubscribeMessages: (() => void) | undefined;

    const serializeMessage = (
      input: ChatMessageEvent | Message
    ): UiMessage | null => {
      // History items are Message, live events are ChatMessageEvent
      const msg = 'message' in input ? input.message : input;

      if (!msg) return null;

      const timestamp = msg.timestamp ?? Date.now();

      return {
        id: msg.id ?? `${timestamp}-${Math.random()}`,
        text: msg.text ?? '',
        time: new Date(timestamp).toLocaleTimeString([], {
          hour: '2-digit',
          minute: '2-digit',
        }),
        timestamp,
        fromMe: msg.clientId?.startsWith('seeker:'),
        serial: msg.serial,
        deleted: false,
      };
    };

    (async () => {
      try {
        setConnected('connecting');

        const { data, error } = await supabase.functions.invoke<{
          tokenRequest: Ably.Types.TokenRequest;
          roomName: string;
        }>('seeker-chat-token', { method: "POST" });

        if (error || !data) {
          throw error ?? new Error('Unable to obtain chat token');
        }

        if (cancelled) return;

        const { tokenRequest, roomName } = data;

        const ably = new Ably.Realtime({
          authCallback: (_params, cb) => cb(null, tokenRequest)
        });
        ablyRef.current = ably;

        const onConnectionChange = (stateChange: Ably.Types.ConnectionStateChange) => {
          if (stateChange.current === 'connected') {
            setConnected('connected');
          } else if (
            stateChange.current === 'failed' ||
            stateChange.current === 'closing' ||
            stateChange.current === 'closed' ||
            stateChange.current === 'suspended'
          ) {
            setConnected('failed');
          }
        };

        ably.connection.on(onConnectionChange);
        unsubscribeConnection = () => ably.connection.off(onConnectionChange);

        const chatClient = new ChatClient(ably);
        chatClientRef.current = chatClient;

        const room = await chatClient.rooms.get(roomName);
        if (cancelled) return;

        roomRef.current = room;
        await room.attach();

        // Load the most recent history (last 7 days)
        try {
          const sevenDaysAgo = Date.now() - 7 * 24 * 60 * 60 * 1000;
          const history = await room.messages.history({
            start: sevenDaysAgo,
            orderBy: OrderBy.OldestFirst,
            limit: 200
          });
          if (!cancelled) {
            const historyMessages = history.items
              .map(serializeMessage)
              .filter((m): m is UiMessage => Boolean(m))
              .sort((a, b) => a.timestamp - b.timestamp);
            setMessages(historyMessages);
          }
        } catch (historyErr) {
          console.warn('Failed to load chat history', historyErr);
        }

        const subscription = room.messages.subscribe((evt: ChatMessageEvent) => {
          const mapped = serializeMessage(evt);
          if (!mapped) return;
          setMessages((prev) => {
            if (prev.some((msg) => msg.id === mapped.id)) {
              return prev;
            }
            const next = [...prev, mapped];
            next.sort((a, b) => a.timestamp - b.timestamp);
            return next;
          });
        });
        unsubscribeMessages = () => subscription.unsubscribe();

        await room.presence.enter({ role: 'seeker' });
        if (!cancelled) {
          setConnected('connected');
        }
      } catch (err) {
        console.error('Failed to initialise chat', err);
        if (!cancelled) {
          setConnected('failed');
        }
      }
    })();

    return () => {
      cancelled = true;
      unsubscribeConnection?.();
      unsubscribeMessages?.();

      (async () => {
        try {
          await roomRef.current?.presence.leave();
        } catch (err) {
          console.warn('Failed to leave presence', err);
        }
        try {
          if (roomRef.current) {
            await chatClientRef.current?.rooms.release(roomRef.current.name);
          }
        } catch (err) {
          console.warn('Failed to release room', err);
        }
        try {
          ablyRef.current?.close();
        } catch (err) {
          console.warn('Failed to close Ably connection', err);
        }
        roomRef.current = null;
        chatClientRef.current = null;
        ablyRef.current = null;
      })();
    };
  }, [supabase]);

  // Poll room occupancy to determine online status
  useEffect(() => {
    if (!roomRef.current || connected !== 'connected') {
      console.log('[Seeker Chat] Occupancy check skipped:', { hasRoom: !!roomRef.current, connected });
      return;
    }

    let cancelled = false;

    const pollOccupancy = async () => {
      // Don't proceed if component unmounted or connection closed
      if (cancelled || !roomRef.current || connected !== 'connected') {
        return;
      }

      try {
        // Use presence to count connections instead of occupancy API
        const presenceMembers = await roomRef.current?.presence.get();

        // Check again after async operation
        if (cancelled) return;

        console.log('[Seeker Chat] Presence members:', presenceMembers);

        const connections = presenceMembers?.length ?? 0;
        setOccupancyCount(connections);
      } catch (err) {
        if (cancelled) return;
        console.warn('Failed to get presence members', err);
        // If presence check fails, assume user is alone
        setOccupancyCount(1);
      }
    };

    // Poll immediately
    pollOccupancy();

    // Then poll every 5 seconds
    const intervalId = setInterval(pollOccupancy, 5000);

    return () => {
      cancelled = true;
      clearInterval(intervalId);
    };
  }, [connected]);

  const handleSend = async () => {
    const trimmed = input.trim();
    if (!trimmed) return;

    setInput('');
    try {
      await roomRef.current?.messages.send({ text: trimmed });

      // Only send notification if guide is not in the room (occupancy < 2)
      if (guide?.id && occupancyCount < 2) {
        try {
          await supabase.functions.invoke('send-message-notification', {
            body: { receiver_id: guide.id }
          });
        } catch (notifErr) {
          console.warn('Failed to send message notification', notifErr);
        }
      }
    } catch (err) {
      console.error('Failed to send chat message', err);
      setInput(trimmed);
    }
  };

  const handleDeleteMessage = async (message: UiMessage) => {
    if (!message.serial || !roomRef.current) return;

    try {
      // Mark as deleted in UI immediately
      setMessages((prev) =>
        prev.map((msg) =>
          msg.id === message.id ? { ...msg, deleted: true, text: 'Message deleted' } : msg
        )
      );

      // Delete from Ably
      await roomRef.current.messages.delete(message.serial, {
        description: 'Message deleted by user',
      });
    } catch (err) {
      console.error('Failed to delete message', err);
      // Revert the UI change on error
      setMessages((prev) =>
        prev.map((msg) =>
          msg.id === message.id ? { ...msg, deleted: false } : msg
        )
      );
    }
  };


  const seekerAvatarSrc = seekerProfile?.imageUrl || undefined;
  const seekerInitials = useMemo(() => getInitials(seekerProfile?.name || 'You'), [seekerProfile?.name]);

  const messageNodes = useMemo(
    () =>
      messages.map((message) => {
        const isFromGuide = !message.fromMe;
        const isDeleted = message.deleted;
        return (
          <div
            key={message.id}
            className={`flex items-start gap-3 ${isFromGuide ? '' : 'flex-row-reverse'}`}
          >
            <Avatar
              src={isFromGuide ? (hasGuideImage ? guideAvatarSrc : undefined) : seekerAvatarSrc}
              alt={isFromGuide ? guideName : 'You'}
              fallback={isFromGuide ? guideInitials : seekerInitials}
            />
            <div className={`flex max-w-[314px] flex-col ${isFromGuide ? '' : 'items-end'}`}>
              <div
                className={`w-full rounded-lg px-4 py-3 text-sm shadow-sm ${
                  isDeleted
                    ? 'bg-[#304031]/10 text-[#304031]/40 italic'
                    : isFromGuide
                    ? 'bg-[#E8DFBD] text-[#2C2731]'
                    : 'bg-[#304031] text-[#FFFCEF]'
                }`}
              >
                {message.text}
              </div>
              <div className={`mt-2 flex items-center gap-2 text-xs ${
                isFromGuide ? 'text-[#2C2731]/70' : 'text-[#304031]/70'
              }`}>
                <span>{message.time}</span>
              </div>
            </div>
          </div>
        );
      }),
    [guideAvatarSrc, guideInitials, guideName, hasGuideImage, messages, seekerAvatarSrc, seekerInitials]
  );

  return (
    <div className="flex h-screen min-h-screen w-full items-stretch overflow-hidden">
      <div className="relative flex h-full w-full flex-1 flex-col overflow-hidden bg-[#FFFCEF]">
        <header className="flex h-16 items-center border-b border-[#FFFCEF]/25 bg-[#2C2731] px-4">
          <button
            type="button"
            aria-label="Back"
            className="inline-flex h-10 w-10 items-center justify-center rounded-2xl border border-[#FFFCEF]/30 bg-transparent text-xl text-[#FFFCEF] transition hover:bg-[#FFFCEF]/10 disabled:cursor-default disabled:opacity-60"
            onClick={onBack}
            disabled={!onBack}
          >
            {'\u2190'}
          </button>

          <div className="flex flex-1 items-center justify-center gap-3">
            <Avatar
              src={hasGuideImage ? guideAvatarSrc : undefined}
              alt={guideName}
              fallback={guideInitials}
            />
            <div className="flex flex-col">
              <span className="text-base font-medium text-[#FFFCEF] ph-mask">{guideName}</span>
              <span className="text-xs text-[#FFFCEF]/70">
                {connected === 'connected'
                  ? occupancyCount >= 2
                    ? 'Online now'
                    : 'Offline'
                  : connected === 'connecting'
                  ? 'Connecting…'
                  : 'Offline'}
              </span>
            </div>
          </div>

          <div className="w-10" aria-hidden />
        </header>

        <main className="flex flex-1 min-h-0 flex-col">
          <div className="mx-auto flex h-full w-full max-w-2xl flex-1 min-h-0 flex-col gap-4 px-6 pb-6 pt-4">
          <div className="shrink-0 rounded-xl bg-[#304031] px-4 py-3 text-center text-sm font-medium text-[#FFFCEF] shadow-sm">
            Secure Messaging · Chat history kept for 7 days
          </div>

          <div className="flex flex-1 min-h-0 flex-col overflow-hidden rounded-2xl border border-[#2C2731] bg-[#FFFCEF] shadow-sm">
            <div ref={listRef} className="ph-no-capture flex-1 overflow-y-auto px-4 py-6">
              {connected === 'failed' ? (
                <p className="mx-auto mb-10 max-w-[324px] rounded-md bg-red-50 px-3 py-2 text-center text-sm text-red-600">
                  We couldn&apos;t connect to chat. Please try again.
                </p>
              ) : !messages.length ? (
                <p className="mx-auto mb-10 max-w-[324px] text-center text-sm text-[#2C2731]/70">
                  Say hello to your guide! You can chat anytime.
                </p>
              ) : null}
              <div className="space-y-6">{messageNodes}</div>
            </div>

            <div className="border-t border-[#2C2731]/20 px-4 py-4">
              <div className="flex items-center gap-3">
                <div className="flex-1 rounded-lg bg-[#E8DFBD] px-4 py-2.5">
                  <input
                    value={input}
                    onChange={(event) => setInput(event.target.value)}
                    placeholder="Type your message..."
                    className="w-full bg-transparent text-sm text-[#2C2731] placeholder:text-[#2C2731]/50 focus:outline-none"
                    onKeyDown={(event) => {
                      if (event.key === 'Enter') {
                        event.preventDefault();
                        handleSend();
                      }
                    }}
                    disabled={connected !== 'connected'}
                  />
                </div>
                <button
                  type="button"
                  onClick={handleSend}
                  disabled={connected !== 'connected'}
                  className="flex h-10 w-10 items-center justify-center rounded-lg bg-[#304031] text-[#FFFCEF] hover:bg-[#304031]/90 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#FFFCEF] focus-visible:ring-offset-2 focus-visible:ring-offset-[#304031] disabled:opacity-50"
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M22 2 11 13" />
                    <path d="m22 2-7 20-2-7-7-13z" />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        </div>
        </main>

        <nav className="shrink-0 border-t border-[#FEF6D6]/40 bg-[#304031]">
          <div className="mx-auto max-w-sm px-4 py-2">
            <div className="flex justify-around">
              {tabDefinitions.map((tab) => {
                const isActive = activeTab === tab.key;
                return (
                  <button
                    key={tab.key}
                    type="button"
                    onClick={() => handleTabClick(tab.path)}
                    className={`relative flex flex-col items-center px-3 py-2 transition-colors ${
                      isActive ? 'text-[#FEF6D6]' : 'text-[#FEF6D6]/60'
                    }`}
                  >
                    <span className={`mb-1 ${isActive ? 'text-[#FEF6D6]' : 'text-[#FEF6D6]/60'}`}>
                      {tab.icon}
                    </span>
                    <span
                      className={`text-xs ${
                        isActive ? 'font-medium text-[#FEF6D6]' : 'text-[#FEF6D6]/60'
                      }`}
                    >
                      {tab.label}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        </nav>
      </div>
    </div>
  );
}
